import type { ScreenContext } from "../../shell/types";
import { api, el, toast, together } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { table } from "../../ui/record";
import { field, refuse, select } from "../clients/forms";
import { pageHead } from "../empty";
import { ptoCards, type PtoSettings } from "./pto";

// Settings › Week, overtime & PTO (brief 1D-5, mockup v49 settings/time). Only the Week card is
// built here: the day the workweek starts (a change asks first, takes effect from the next full
// week and is kept with its date: "Starts Sunday until Nov 1, then Monday"), the time zone, and
// the billing week per client type. The Overtime card (brief 1E-4, mockup v56): overtime after n
// hours in the week, paid at a multiplier, the scheduler warned at n hours, daily overtime off or
// after n hours, projected overtime on the schedule, and hours counted across the company's modules
// (only when it has more than one). Brief 1E-5 (mockup v58): how overtime pay is worked out (the
// weighted average of the week's rates, or the rate of the work with the person's agreement), and
// the note that a person's own threshold is set on their pay package. Brief 1E-11 (mockup v61): the
// minimum wage the week summary checks against (on the Overtime card), and the paid time off cards
// (settings/pto.ts). Brief 1E-33: the pay period, weekly or every two weeks from a date. Admin only.

interface Week {
  start: string;
  waiting: { start: string; startsOn: string; label: string } | null;
  words: string;
  /** "Nov 1 is a 1-day week, then weeks start Monday", while a change waits (brief 1D-6). */
  changeover: string | null;
  days: { value: string; label: string }[];
  timeZone: string;
  timeZones: { value: string; label: string }[];
  history: { at: string; by: string; from: string; to: string; startsOn: string }[];
  clientTypes: { id: string; name: string; billingWeek: string | null }[];
  /** Brief 1E-33: weekly or every two weeks from a date. */
  payPeriod: {
    every: string;
    from: string | null;
    words: string;
    now: string;
    choices: { value: string; label: string }[];
  };
}

interface Overtime {
  overtime: {
    afterHours: number;
    multiplier: number;
    warnHours: number;
    dailyAfter: number | null;
    acrossModules: boolean;
    onSchedule: boolean;
    method: string;
  };
  words: string;
  modules: string[];
  methods: { value: string; label: string }[];
}

let otSeq = 0;
function numberField(label: string, value: string, type = "number") {
  const id = `ot${++otSeq}`;
  const input = el("input", { id, type, value, inputmode: "decimal" });
  return { el: el("div", { class: "field" }, el("label", { for: id }, label), input), input };
}
function tick(label: string, on: boolean) {
  const input = el("input", { type: "checkbox", checked: on });
  return { el: el("label", { class: "ptask" }, input, ` ${label}`), input };
}

/** The Overtime card; `read` answers what changed, or null; `wage` the minimum wage, if changed. */
function overtimeCard(o: Overtime, minimumWage: string | null) {
  const s = o.overtime;
  const after = numberField("Overtime after (hours in the week)", String(s.afterHours));
  const paid = numberField("Paid at", `${s.multiplier}×`, "text");
  const warn = numberField("Warn the scheduler at (hours)", String(s.warnHours));
  const dailyChoices: [string, string][] = [
    ["", "Off"],
    ["8", "After 8 hours a day"],
    ["12", "After 12 hours a day"],
  ];
  if (s.dailyAfter && !["8", "12"].includes(String(s.dailyAfter)))
    dailyChoices.push([String(s.dailyAfter), `After ${s.dailyAfter} hours a day`]);
  const daily = select("Daily overtime", dailyChoices, s.dailyAfter ? String(s.dailyAfter) : "");
  const method = select(
    "How overtime pay is worked out",
    o.methods.map((m) => [m.value, m.label] as [string, string]),
    s.method,
  );
  const methodHint = el(
    "span",
    { class: "small muted" },
    "For someone paid more than one rate in a week. The second way is allowed only when the person agreed to it in advance.",
  );
  method.el.append(methodHint);
  const onSchedule = tick(
    "Show projected overtime on the schedule (roles with Sees financials)",
    s.onSchedule,
  );
  const across = tick(
    `Count hours across ${o.modules.join(" and ")} for the same person`,
    s.acrossModules,
  );
  // Brief 1E-11: the minimum wage an hour, a number the company sets.
  const wage = numberField("Minimum wage (per hour)", minimumWage ?? "");
  wage.input.placeholder = "15.00";
  wage.el.append(
    el("span", { class: "small muted" }, "The week summary flags an average per hour under it."),
  );
  const el_ = el(
    "section",
    { class: "card", "aria-label": "Overtime" },
    el("h2", {}, icon("clock"), " Overtime"),
    el(
      "form",
      { class: "form", style: "max-width:none" },
      el("div", { class: "grid2", style: "gap:8px" }, after.el, paid.el, warn.el, daily.el),
      method.el,
      wage.el,
      onSchedule.el,
      ...(o.modules.length > 1 ? [across.el] : []),
    ),
    el(
      "p",
      { class: "small muted" },
      "A person's own overtime start, or exemption, is set on their pay package.",
    ),
  );
  const read = () => {
    const want = {
      afterHours: after.input.value,
      multiplier: paid.input.value,
      warnHours: warn.input.value,
      dailyAfter: daily.input.value || null,
      onSchedule: onSchedule.input.checked,
      acrossModules: across.input.checked,
      method: method.input.value,
    };
    const same =
      Number(want.afterHours) === s.afterHours &&
      Number(want.multiplier.replace(/[×x\s]/g, "")) === s.multiplier &&
      Number(want.warnHours) === s.warnHours &&
      (want.dailyAfter === null ? null : Number(want.dailyAfter)) === s.dailyAfter &&
      want.onSchedule === s.onSchedule &&
      want.acrossModules === s.acrossModules &&
      want.method === s.method;
    return same ? null : want;
  };
  const readWage = () => {
    const v = wage.input.value.trim();
    const had = minimumWage ?? "";
    return v === had || (v !== "" && had !== "" && Number(v) === Number(had)) ? null : { value: v };
  };
  return { el: el_, read, readWage };
}

export async function weekScreen(host: HTMLElement, ctx: ScreenContext) {
  const save = el("button", { type: "button", class: "btn pri" }, "Save");
  host.append(
    pageHead(
      ctx,
      "When the week starts, when overtime begins, and how paid time off and sick leave are earned.",
      [save],
      "Company settings",
    ),
  );
  const slot = el("div");
  const ptoSlot = el("div");
  host.append(slot, ptoSlot);
  let pto: PtoSettings | null = null;
  let w: Week;
  let ot: Overtime | null = null;
  let otCard: ReturnType<typeof overtimeCard> | null = null;
  let pick: () => { start: string; zone: string; billing: Map<string, string | null> } = () => ({
    start: w.waiting?.start ?? w.start,
    zone: w.timeZone,
    billing: new Map(),
  });
  let payPick: () => { every: string; from: string } | null = () => null;

  async function load() {
    // One call for the screen (brief 1E-29).
    const [res, o, p] = await together(() =>
      Promise.all([
        api<Week>("GET", "/api/settings/week"),
        api<Overtime>("GET", "/api/settings/overtime"),
        api<PtoSettings>("GET", "/api/settings/pto"),
      ]),
    );
    ot = o.ok ? o.body : null;
    pto = p.ok ? p.body : null;
    if (pto) void ptoCards(ptoSlot, pto);
    if (!res.ok) {
      slot.replaceChildren(el("p", { class: "note", role: "alert" }, "The week needs Admin."));
      return;
    }
    w = res.body;
    draw();
  }

  function draw() {
    const start = select(
      "Workweek starts on",
      w.days.map((d) => [d.value, d.label] as const),
      w.waiting?.start ?? w.start,
    );
    start.input.id = "wk1";
    start.el.querySelector("label")?.setAttribute("for", "wk1");
    start.el.append(
      el(
        "span",
        { class: "small muted" },
        "Sets overtime, payroll periods and the first day on every schedule.",
      ),
    );
    // The US zones by name (brief 1E-9); a zone set before the list stays shown as it is.
    const zones = w.timeZones.map((z) => [z.value, z.label] as [string, string]);
    if (!zones.some(([v]) => v === w.timeZone)) zones.unshift([w.timeZone, w.timeZone]);
    const zone = select("Time zone", zones, w.timeZone);
    zone.input.id = "wk2";
    zone.el.querySelector("label")?.setAttribute("for", "wk2");
    zone.el.append(
      el(
        "span",
        { class: "small muted" },
        "New schedules and records use it. Saved times stay as they are.",
      ),
    );
    const billing = new Map<string, string | null>();
    const bills = w.clientTypes.map((t) => {
      const s = el(
        "select",
        { "aria-label": `Billing week for ${t.name}` },
        el("option", { value: "", selected: !t.billingWeek }, "Company workweek"),
        ...w.days.map((d) =>
          el("option", { value: d.value, selected: t.billingWeek === d.value }, `${d.label} start`),
        ),
      );
      s.addEventListener("change", () => billing.set(t.id, s.value || null));
      return { t, s };
    });
    pick = () => ({ start: start.input.value, zone: zone.input.value, billing });
    // Brief 1E-33: the pay period (payroll, Earnings and the caregiver's Earned follow it).
    const every = select(
      "Pay period",
      w.payPeriod.choices.map((c) => [c.value, c.label] as const),
      w.payPeriod.every,
    );
    every.input.id = "wk3";
    every.el.querySelector("label")?.setAttribute("for", "wk3");
    every.el.append(
      el(
        "span",
        { class: "small muted" },
        "Payroll, Earnings and the caregiver's Earned follow it.",
      ),
    );
    const from = field("First pay period", w.payPeriod.from, { placeholder: "MM/DD/YYYY" });
    from.input.id = "wk4";
    from.el.querySelector("label")?.setAttribute("for", "wk4");
    const showFrom = () => (from.el.hidden = every.input.value !== "two_weeks");
    every.input.addEventListener("change", showFrom);
    showFrom();
    payPick = () => {
      const want = { every: every.input.value, from: from.input.value.trim() };
      const same =
        want.every === w.payPeriod.every &&
        (want.every === "week" || want.from === (w.payPeriod.from ?? ""));
      return same ? null : want;
    };
    otCard = ot ? overtimeCard(ot, pto?.minimumWage ?? null) : null;
    slot.replaceChildren(
      el(
        "div",
        { class: "grid2" },
        el(
          "section",
          { class: "card", "aria-label": "Week" },
          el("h2", {}, icon("calendar"), " Week"),
          el(
            "p",
            { class: "small", "data-week-words": "1", style: "margin:0 0 8px" },
            el("b", {}, w.words),
          ),
          w.changeover
            ? el(
                "p",
                { class: "small muted", "data-week-changeover": "1", style: "margin:-4px 0 8px" },
                w.changeover,
              )
            : "",
          el(
            "form",
            { class: "form", style: "max-width:none" },
            start.el,
            zone.el,
            every.el,
            from.el,
            el(
              "p",
              { class: "small muted", "data-pay-period": "1", style: "margin:0" },
              w.payPeriod.now,
            ),
          ),
          ...(bills.length
            ? [
                el("h3", { style: "margin-top:12px" }, "Billing week"),
                el(
                  "p",
                  { class: "small muted" },
                  "Invoices follow the workweek unless a payer runs its own week. Set it on the client type or the payer.",
                ),
                table(
                  [
                    { h: "Client type", f: (b) => el("b", {}, b.t.name) },
                    { h: "Bills by", f: (b) => b.s },
                  ],
                  bills,
                ),
              ]
            : []),
        ),
        otCard?.el ?? "",
      ),
    );
  }

  save.addEventListener("click", async () => {
    // The minimum wage (brief 1E-11), its own save.
    const wageWant = otCard?.readWage();
    let wageSaved: string | null = null;
    if (wageWant) {
      save.disabled = true;
      const r = await api<PtoSettings>("PUT", "/api/settings/minimum-wage", {
        minimumWage: wageWant.value,
      });
      save.disabled = false;
      if (!r.ok) return toast("The minimum wage wasn't saved", r.body.error);
      pto = r.body;
      wageSaved = pto.minimumWage
        ? `Minimum wage $${pto.minimumWage} an hour`
        : "No minimum wage set";
    }
    // The Overtime card first: its own save, said in the same toast.
    const otWant = otCard?.read();
    if (otWant) {
      save.disabled = true;
      const r = await api<Overtime>("PUT", "/api/settings/overtime", otWant);
      save.disabled = false;
      if (!r.ok) return toast("Overtime wasn't saved", r.body.error);
      if (ot) ot = { ...ot, overtime: r.body.overtime, words: r.body.words };
    }
    // The pay period (brief 1E-33), its own save, said in the toast.
    const payWant = payPick();
    let paySaved: string | null = null;
    if (payWant) {
      save.disabled = true;
      const r = await api<Week>("PUT", "/api/settings/pay-period", payWant);
      save.disabled = false;
      if (!r.ok) return toast("The pay period wasn't saved", r.body.error);
      w = r.body;
      paySaved = `Pay period: ${w.payPeriod.words}`;
    }
    const want = pick();
    let zoneSaved: string | null = null;
    if (want.zone !== w.timeZone) {
      save.disabled = true;
      const r = await api<Week>("PUT", "/api/settings/time-zone", { timeZone: want.zone });
      save.disabled = false;
      if (!r.ok) return toast("The time zone wasn't saved", r.body.error);
      w = r.body;
      zoneSaved = `Time zone is ${w.timeZones.find((z) => z.value === w.timeZone)?.label ?? w.timeZone}`;
    }
    const current = w.waiting?.start ?? w.start;
    const changes = [...want.billing].filter(
      ([id, v]) => (w.clientTypes.find((t) => t.id === id)?.billingWeek ?? null) !== v,
    );
    const saveBilling = async () => {
      for (const [id, billingWeek] of changes) {
        const r = await api<Week>("PUT", `/api/settings/client-types/${id}/billing-week`, {
          billingWeek,
        });
        if (!r.ok) return toast("That wasn't saved", r.body.error);
        w = r.body;
      }
      return null;
    };
    if (want.start === current) {
      if (!changes.length) {
        if (otWant || zoneSaved || wageSaved || paySaved) draw();
        if (paySaved) return toast("Saved", paySaved);
        if (zoneSaved) return toast("Saved", zoneSaved);
        if (wageSaved && !otWant) return toast("Saved", wageSaved);
        return toast("Saved", otWant ? (ot?.words ?? "Overtime saved") : "Nothing changed");
      }
      save.disabled = true;
      await saveBilling();
      save.disabled = false;
      draw();
      return toast("Saved", "Billing weeks are set");
    }
    // A new start asks first: it takes effect from the next full week, and is logged.
    const label = w.days.find((d) => d.value === want.start)?.label ?? want.start;
    const go = el("button", { type: "button", class: "btn pri" }, icon("check"), ` Start ${label}`);
    const m = openModal({
      title: `Start the week on ${label}?`,
      sub: "It takes effect from the next full week. This week and earlier weeks keep the start they had.",
      body: [],
      actions: [go],
    });
    go.addEventListener("click", async () => {
      go.disabled = true;
      const r = await api<Week>("PUT", "/api/settings/week", { start: want.start });
      go.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      w = r.body;
      await saveBilling();
      m.close();
      draw();
      toast("Saved", w.words);
    });
  });

  await load();
}
